import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { formatTime } from '../../musicVideoEditor/timeline/timelineGeometry';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { directedMessages as messages } from '../messages/directedMessages';
import { SectionMapBar } from './SectionMapBar';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import styles from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DirectedSongFactsProps {
  /** The cards of the step under the facts (design b1: Roles, How it is used). */
  children?: React.ReactNode;
}

/**
 * The side panel "What the director reads" (design b1): the song, its length and tempo, the lyrics,
 * the sections, the cards of the step, and the note that the steps up to the price cost nothing.
 */
const DirectedSongFactsComponent: React.FC<DirectedSongFactsProps> = ({ children }) => {
  const intl = useIntl();
  const { song } = useDirectedVideoContext();
  let lyrics: string;
  if (song.lyricsStatus === 'synced') {
    lyrics = intl.formatMessage(messages.lyricsSynced, { count: song.lyricLineCount });
  } else if (song.lyricsStatus === 'syncing') {
    lyrics = intl.formatMessage(messages.lyricsSyncing);
  } else {
    lyrics = intl.formatMessage(messages.lyricsNone);
  }
  let length: string | null = null;
  if (song.durationSec !== null) {
    length =
      song.bpm === null
        ? formatTime(song.durationSec)
        : intl.formatMessage(messages.factLengthBpm, {
            length: formatTime(song.durationSec),
            bpm: Math.round(song.bpm),
          });
  }

  return (
    <>
      <h4>{intl.formatMessage(messages.sideTitle)}</h4>
      <dl className={styles.facts}>
        <dt>{intl.formatMessage(messages.factSong)}</dt>
        <dd>{intl.formatMessage(messages.factSongValue, { title: song.title, artist: song.artistName })}</dd>
        {length !== null && (
          <>
            <dt>{intl.formatMessage(messages.factLength)}</dt>
            <dd className={styles.mono}>{length}</dd>
          </>
        )}
        <dt>{intl.formatMessage(messages.factLyrics)}</dt>
        <dd>{lyrics}</dd>
      </dl>
      {song.sections.length > 0 && (
        <>
          <span className={styles.eyebrow}>{intl.formatMessage(messages.sections)}</span>
          <SectionMapBar sections={song.sections} />
        </>
      )}
      {children !== undefined && <div className={styles.separator} />}
      {children}
      <span className={styles.grow} />
      <p className={styles.hint}>{intl.formatMessage(messages.sideHint)}</p>
    </>
  );
};

export const DirectedSongFacts = TypedMemo(DirectedSongFactsComponent);
